import { Injectable, inject } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ModalService } from 'tms-lib';

import { PurchaseOrderLineDetails } from '../../models/purchasing/purchase-order-line-details';
import { SalesOrderLineDetails } from '../../models/sales/sales-order-line-details';
import { SalesOrderLineService } from '../sales/sales-order-line.service';
import { PurchaseOrderLineService } from './purchase-order-line.service';

/**
 * Opens the sales order line / purchase order line modals from screens that live INSIDE those
 * modals.
 *
 * ⚠️ Why this exists - the modals are loaded with a dynamic `import()` on purpose. The purchase
 * order line modal hosts the Order Line details tab and both allocation tabs; the sales order line
 * modal hosts a BOM tab that opens the purchase order line modal. So a plain
 * `import { PurchaseOrderLineModalComponent }` from any of those tabs is a circular import:
 * modal -> tab -> modal. It lints and compiles, and whether it works at runtime depends on which
 * file the bundle happens to evaluate first - the loser sees an uninitialised class in its
 * `imports` array. A dynamic import resolves at click time, when everything is loaded, and
 * returns the same module instance the static importers use.
 *
 * Same flow as the Stock Enquiry Demand grid, which can import the modals statically because
 * nothing inside them imports Stock Enquiry: load the line, open the XL modal, hand the modal
 * back so the CALLER refreshes behind it on close (ui.md: refreshing an open document is a
 * per-call-site duty).
 */
@Injectable({ providedIn: 'root' })
export class OrderLineModalService {
  private readonly modalService = inject(ModalService);
  private readonly salesOrderLineService = inject(SalesOrderLineService);
  private readonly purchaseOrderLineService = inject(PurchaseOrderLineService);

  openSalesOrderLine(salesOrderLineId: string): Observable<NgbModalRef> {
    return this.salesOrderLineService.edit(salesOrderLineId).pipe(
      switchMap((salesOrderLine: SalesOrderLineDetails) =>
        from(import('../../components/sales/sales-order-line-modal/sales-order-line-modal.component')).pipe(
          switchMap(({ SalesOrderLineModalComponent }) => {
            const { modal, component } =
              this.modalService.openXL<InstanceType<typeof SalesOrderLineModalComponent>>(SalesOrderLineModalComponent);
            component.salesOrderLineId.set(salesOrderLine.id);
            component.salesOrderHeaderId.set(salesOrderLine.salesOrderHeaderId);
            component.lineSequence.set(salesOrderLine.sequence);
            component.productKey.set(salesOrderLine.productKey);
            return [modal];
          }),
        ),
      ),
    );
  }

  openPurchaseOrderLine(purchaseOrderLineId: string): Observable<NgbModalRef> {
    return this.purchaseOrderLineService.edit(purchaseOrderLineId).pipe(
      switchMap((purchaseOrderLine: PurchaseOrderLineDetails) =>
        from(import('../../components/purchasing/purchase-order-line-modal/purchase-order-line-modal.component')).pipe(
          switchMap(({ PurchaseOrderLineModalComponent }) => {
            const { modal, component } = this.modalService.openXL<InstanceType<typeof PurchaseOrderLineModalComponent>>(
              PurchaseOrderLineModalComponent,
            );
            component.purchaseOrderLineId.set(purchaseOrderLine.id);
            component.purchaseOrderHeaderId.set(purchaseOrderLine.purchaseOrderHeaderId);
            component.lineSequence.set(purchaseOrderLine.sequence);
            return [modal];
          }),
        ),
      ),
    );
  }
}
